// TradeActionModal — Move, Call off, Cancel or Done on one visit (Oct 28, 2026).
//
// Move edits the same Google event (DATE CHANGE in the title, like a build) and Google tells the guests. The
// homeowner hears only when the new day is outside "give or take a day" from the day they were told (Greg:
// "I know my gutter team is not always punctual. It's usually +/- a day"); the window previews exactly what
// goes, or says nothing will. Cancel takes it off for good; no text was approved for that, so the window
// reminds whoever cancels to tell the homeowner if they'd been told a day.

import { useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  afterRoofTextProblem, detachTextProblem, fmtTradeDay, moveTextKind, textCanNameWork, tradeCall, tradeCalledOffText, tradeMovedText, tradeScheduledText,
  type Trade, type TradeCalendarResult, type TradeMoveResult,
} from "../../lib/trades";
import { firstNameForText, smsInfo } from "../../../supabase/functions/build-calendar/eventText";
import { CalendarStepText, StepRow, TextStepText, calendarOk, textOk, workLabel } from "./tradeUi";

export type TradeAction = "move" | "call_off" | "cancel" | "done";

const REASONS = ["The sub's schedule", "Weather", "The roof moved", "Homeowner asked", "Other"];
const ARRIVALS = ["", "AM", "PM"];

interface Props {
  trade: Trade;
  action: TradeAction;
  today: string;
  onClose: () => void;
  onDone: () => void;
}

export default function TradeActionModal({ trade: t, action, today, onClose, onDone }: Props) {
  const [date, setDate] = useState(action === "done" ? today : "");
  const [arrival, setArrival] = useState(t.arrival_window ?? "");
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const [textHo, setTextHo] = useState(true);
  const [phone, setPhone] = useState<{ ok: boolean; optedOut: boolean } | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [moved, setMoved] = useState<TradeMoveResult | null>(null);
  const [cancelled, setCancelled] = useState<{ calendar: TradeCalendarResult; homeowner_told_date: string | null } | null>(null);

  useEffect(() => {
    if (action !== "move" && action !== "call_off") return;
    let alive = true;
    supabase.from("deals").select("homeowner_phone,sms_opt_out").eq("id", t.deal_id).maybeSingle().then(({ data }) => {
      if (!alive) return;
      const digits = String((data?.homeowner_phone as string | null) ?? "").replace(/\D/g, "");
      setPhone({ ok: digits.length >= 10, optedOut: data?.sms_opt_out === true });
    });
    return () => { alive = false; };
  }, [action, t.deal_id]);

  const newDate = action === "call_off" ? null : date || null;
  const rawKind = action === "move" || action === "call_off"
    ? (newDate === t.scheduled_date ? "none" : moveTextKind(t.homeowner_told_date, newDate))
    : "none";
  // A first text needs approved wording that can name the work (review, Oct 29); a move or call-off text only
  // goes to a homeowner already told, so those stay.
  const roofStart = t.deal?.build_scheduled_date?.slice(0, 10) ?? null;
  const roofEnd = t.deal?.build_end_date?.slice(0, 10) ?? null;
  const afterRoofWording = t.trade_type === "reset" || (t.trade_type === "new_install" && t.timing === "after_build");
  const firstTextProblem = rawKind === "scheduled"
    ? (!textCanNameWork(t.trade_type, t.items) ? "No approved text names this work yet. Let the homeowner know yourself."
      : t.trade_type === "detach" && !roofStart ? "The text for a detach with no roof date booked isn't approved yet. Let the homeowner know yourself."
        : t.trade_type === "detach" ? detachTextProblem(newDate, roofStart)
          : afterRoofWording ? afterRoofTextProblem(newDate, roofStart, roofEnd) : null)
    : null;
  const kind = firstTextProblem ? "none" : rawKind;
  const preview = useMemo(() => {
    const first = firstNameForText(t.deal?.homeowner_name);
    if (kind === "scheduled" && newDate) {
      return tradeScheduledText({ firstName: first, street: t.deal?.property_street ?? null, roofStart, roofEnd },
        { work: t.trade_type, items: t.items, timing: t.timing, date: newDate }) ?? "";
    }
    if (kind === "moved" && newDate) return tradeMovedText(first, t.items, newDate, t.trade_type);
    if (kind === "called_off" && t.homeowner_told_date) return tradeCalledOffText(first, t.items, t.homeowner_told_date, t.trade_type);
    return "";
  }, [kind, newDate, t, roofStart, roofEnd]);
  const canText = !!phone && phone.ok && !phone.optedOut;

  const fullReason = [reason, note.trim()].filter(Boolean).join(": ");
  const ready =
    action === "move" ? !!date && date >= today && (date !== t.scheduled_date || arrival !== (t.arrival_window ?? ""))
      : action === "call_off" ? true
        : action === "cancel" ? fullReason.length >= 3
          : !!date && date <= today;

  async function go() {
    setErr(null);
    setBusy(true);
    try {
      if (action === "move" || action === "call_off") {
        setMoved(await tradeCall<TradeMoveResult>({
          action: "trade_move", trade_id: t.id, new_date: newDate, arrival: arrival || null, reason: fullReason || null,
          // The box as ticked; the server checks the phone and STOP itself (it answers "not_sent" with the reason).
          // The deals read above may not be back when Robert taps Call it off (review, Oct 29).
          text_homeowner: textHo,
        }));
      } else if (action === "cancel") {
        setCancelled(await tradeCall<{ ok: boolean; calendar: TradeCalendarResult; homeowner_told_date: string | null }>({ action: "trade_cancel", trade_id: t.id, reason: fullReason }));
      } else {
        await tradeCall({ action: "trade_done", trade_id: t.id, completed_date: date });
        onDone();
        onClose();
        return;
      }
      onDone();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const title = action === "move" ? (t.scheduled_date ? "Move this visit" : "Give it a day")
    : action === "call_off" ? "Call it off (no day yet)" : action === "cancel" ? "Cancel this trade" : "Mark it done";
  const finished = !!moved || !!cancelled;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 sm:p-4" onClick={() => !busy && onClose()} role="dialog" aria-modal="true" aria-label={title}>
      <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-xl shadow-xl flex flex-col max-h-[92vh]" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200">
          <h2 className="text-lg font-semibold text-bardin-ink">{title}</h2>
          <p className="text-sm text-slate-500 mt-0.5 truncate">
            {workLabel(t)} · {t.deal?.homeowner_name ?? ""}{t.scheduled_date ? ` · now ${fmtTradeDay(t.scheduled_date)}` : " · no day yet"}
          </p>
        </div>

        <div className="px-5 py-4 space-y-3 overflow-y-auto text-sm">
          {!finished && (
            <>
              {(action === "move" || action === "done") && (
                <label className="block">
                  <span className="block text-sm font-medium text-slate-700 mb-1">{action === "done" ? "Done on" : "New day"}</span>
                  <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                         {...(action === "done" ? { max: today } : { min: today })}
                         className="border border-slate-300 rounded px-3 py-2 text-sm" />
                </label>
              )}
              {action === "move" && (
                <div className="flex items-center gap-1.5">
                  <span className="text-[11px] text-slate-500">Arrival:</span>
                  {ARRIVALS.map((a) => (
                    <button key={a || "all"} type="button" onClick={() => setArrival(a)}
                            className={"px-2.5 py-1 rounded-full border text-xs font-medium " + ((arrival || "") === a ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200")}>
                      {a || "All day"}
                    </button>
                  ))}
                </div>
              )}
              {action !== "done" && (
                <div>
                  <span className="block text-sm font-medium text-slate-700 mb-1">Why{action === "cancel" ? "" : " (optional)"}</span>
                  <div className="flex flex-wrap gap-1.5 mb-1.5">
                    {REASONS.map((r) => (
                      <button key={r} type="button" onClick={() => setReason(reason === r ? "" : r)}
                              className={"px-2.5 py-1 rounded-full border text-xs font-medium " + (reason === r ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-200")}>
                        {r}
                      </button>
                    ))}
                  </div>
                  <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="A note (stays in the CRM)" className="w-full border border-slate-300 rounded px-3 py-2 text-sm" />
                </div>
              )}

              {(action === "move" || action === "call_off") && (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    The event on Other Trades is {action === "move" ? (t.scheduled_date ? "moved and marked DATE CHANGE" : "added") : "marked CALLED OFF"}, and Google tells everyone on it.
                  </p>
                  {kind === "none" ? (
                    <p className="text-xs text-slate-500">
                      {firstTextProblem
                        ? `No homeowner text: ${firstTextProblem}`
                        : t.homeowner_told_date && newDate
                          ? `No text: the homeowner was told ${fmtTradeDay(t.homeowner_told_date)}, give or take a day.`
                          : "No homeowner text: they weren't told a day."}
                    </p>
                  ) : (
                    <>
                      <label className="flex items-start gap-2">
                        <input type="checkbox" checked={textHo && canText} disabled={!canText} onChange={(e) => setTextHo(e.target.checked)} className="accent-emerald-600 mt-1" />
                        <span>
                          Text the homeowner
                          {phone && !canText && <span className="block text-xs text-slate-400">{phone.optedOut ? "They replied STOP." : "No phone on file."}</span>}
                        </span>
                      </label>
                      {textHo && canText && preview && (
                        <div className="rounded-xl bg-slate-100 px-3 py-2.5 text-[13px] text-slate-800 leading-snug">
                          {preview}
                          <div className="text-[10px] text-slate-400 mt-1.5">{smsInfo(preview).segments} text{smsInfo(preview).segments === 1 ? "" : "s"}</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {action === "cancel" && (
                <p className="text-xs text-slate-500">
                  {t.calendar_source === "adopted" ? "The event (made by hand) is marked CALLED OFF" : "The event comes off Other Trades"} and Google tells everyone on it.
                  {t.homeowner_told_date && <b className="text-amber-800"> The homeowner was texted {fmtTradeDay(t.homeowner_told_date)}: let them know it's off.</b>}
                </p>
              )}
              {err && <div className="bg-red-50 text-red-700 text-sm p-2 rounded">{err}</div>}
            </>
          )}

          {moved && (
            <div className="space-y-2.5">
              <StepRow ok label="Trade">{moved.trade.new_date ? `Now ${fmtTradeDay(moved.trade.new_date)}` : "Called off, no day yet"}</StepRow>
              <StepRow ok={calendarOk(moved.calendar)} warn={moved.calendar.status === "error"} label="Other Trades calendar"><CalendarStepText c={moved.calendar} /></StepRow>
              <StepRow ok={textOk(moved.homeowner_text) || moved.homeowner_text.status === "skipped"} warn={moved.homeowner_text.status === "failed"} label="Homeowner text">
                <TextStepText r={moved.homeowner_text} />
              </StepRow>
            </div>
          )}
          {cancelled && (
            <div className="space-y-2.5">
              <StepRow ok label="Trade">Cancelled.</StepRow>
              <StepRow ok={calendarOk(cancelled.calendar)} warn={cancelled.calendar.status === "error"} label="Other Trades calendar"><CalendarStepText c={cancelled.calendar} /></StepRow>
              {cancelled.homeowner_told_date && (
                <StepRow ok={false} label="Homeowner">They were texted {fmtTradeDay(cancelled.homeowner_told_date)}. Let them know it's off.</StepRow>
              )}
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-slate-100 bg-slate-50 flex justify-end gap-2 rounded-b-xl">
          {finished ? (
            <button onClick={onClose} className="px-4 py-1.5 rounded bg-slate-800 text-white text-sm font-medium">Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={busy} className="px-3 py-1.5 rounded text-sm text-slate-600 disabled:opacity-50">Back</button>
              <button onClick={go} disabled={!ready || busy}
                      className={`px-4 py-1.5 rounded text-white text-sm font-semibold disabled:opacity-50 ${action === "cancel" ? "bg-rose-600 hover:bg-rose-700" : "bg-blue-600 hover:bg-blue-700"}`}>
                {busy ? "Saving…" : action === "move" ? "Move it" : action === "call_off" ? "Call it off" : action === "cancel" ? "Cancel the trade" : "Mark done"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
